import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import Swal, { SweetAlertIcon } from 'sweetalert2';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface ToastConfig {
  title?: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  action?: string;
  actionIcon?: string;
  onAction?: () => void;
  position?: ToastPosition;
  dismissible?: boolean;
}

export interface Toast {
  id: string;
  title?: string;
  message: string;
  variant: ToastVariant;
  duration: number;
  action?: string;
  actionIcon?: string;
  onAction?: () => void;
  position: ToastPosition;
  dismissible: boolean;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new Subject<Toast[]>();
  private toasts: Toast[] = [];
  private maxToasts = 3;
  private defaultDuration = 10000;
  private defaultPosition: ToastPosition = 'bottom-center';

  toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  constructor() {}

  /**
   * Show a success toast
   */
  success(message: string, config?: Partial<ToastConfig>): string {
    return this.show({
      message,
      variant: 'success',
      ...config
    });
  }

  /**
   * Show an error toast
   */
  error(message: string, config?: Partial<ToastConfig>): string {
    return this.show({
      message,
      variant: 'error',
      duration: 15000, // Errors stay longer
      ...config
    });
  }

  /**
   * Show a warning toast
   */
  warning(message: string, config?: Partial<ToastConfig>): string {
    return this.show({
      message,
      variant: 'warning',
      duration: 15000,
      ...config
    });
  }

  /**
   * Show an info toast
   */
  info(message: string, config?: Partial<ToastConfig>): string {
    return this.show({
      message,
      variant: 'info',
      ...config
    });
  }

  /**
   * Show a toast with custom configuration
   */
  show(config: ToastConfig): string {
    const toast: Toast = {
      id: this.generateId(),
      title: config.title,
      message: config.message,
      variant: config.variant || 'info',
      duration: config.duration ?? this.defaultDuration,
      action: config.action,
      actionIcon: config.actionIcon,
      onAction: config.onAction,
      position: config.position || this.defaultPosition,
      dismissible: config.dismissible ?? true,
      timestamp: Date.now()
    };

    // Add to toasts array
    this.toasts.push(toast);

    // Limit max toasts (remove oldest if exceeded)
    if (this.toasts.length > this.maxToasts) {
      this.toasts.shift();
    }

    // Emit updated toasts
    this.toastsSubject.next([...this.toasts]);

    // Auto-dismiss if duration > 0
    if (toast.duration > 0) {
      setTimeout(() => {
        this.dismiss(toast.id);
      }, toast.duration);
    }

    return toast.id;
  }

  /**
   * Dismiss a specific toast by ID
   */
  dismiss(id: string): void {
    const index = this.toasts.findIndex(t => t.id === id);
    if (index > -1) {
      this.toasts.splice(index, 1);
      this.toastsSubject.next([...this.toasts]);
    }
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    this.toasts = [];
    this.toastsSubject.next([]);
  }

  /**
   * Set maximum number of visible toasts
   */
  setMaxToasts(max: number): void {
    this.maxToasts = max;
  }

  /**
   * Set default toast duration
   */
  setDefaultDuration(duration: number): void {
    this.defaultDuration = duration;
  }

  /**
   * Set default toast position
   */
  setDefaultPosition(position: ToastPosition): void {
    this.defaultPosition = position;
  }

  /**
   * Generate unique toast ID
   */
  private generateId(): string {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ============================================
  // SweetAlert2 Toast Methods (Replacement for MatSnackBar)
  // ============================================

  /**
   * Show a SweetAlert2 toast (replaces snackBar.open)
   * @param message The message to display
   * @param type The type of toast (success, error, warning, info)
   * @param duration Duration in milliseconds (default: 10000)
   */
  spifire(message: string, type: SweetAlertIcon = 'info', duration: number = 10000): void {
    Swal.fire({
      text: message,
      icon: type,
      toast: true,
      position: 'top-end',
      timer: duration,
      timerProgressBar: true,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Show a SweetAlert2 success toast
   */
  spiSuccess(message: string, duration: number = 10000): void {
    this.spifire(message, 'success', duration);
  }

  /**
   * Show a SweetAlert2 error toast
   */
  spiError(message: string, duration: number = 15000): void {
    this.spifire(message, 'error', duration);
  }

  /**
   * Show a SweetAlert2 warning toast
   */
  spiWarning(message: string, duration: number = 15000): void {
    this.spifire(message, 'warning', duration);
  }

  /**
   * Show a SweetAlert2 info toast
   */
  spiInfo(message: string, duration: number = 10000): void {
    this.spifire(message, 'info', duration);
  }
}
